import './styles/SendNotificationForm.css';

import type { FormInstance }                       from 'antd';
import { AppFormComponent }                        from 'components/App/AppForm';
import React                                       from 'react';
import type { RichTextEditorHandle }               from 'components/App/RichTextEditor';
import RichTextEditor                              from 'components/App/RichTextEditor';
import { observer }                                from 'mobx-react';
import { Button }                                  from 'antd';
import { Col }                                     from 'antd';
import { Form }                                    from 'antd';
import { Radio }                                   from 'antd';
import Row                                         from 'components/App/Row';
import Input                                       from 'components/App/IntlInput';
import NotificationModel                           from 'models/NotificationModel';
import NotificationVariableModel                   from 'models/NotificationVariableModel';
import { ApiCollection }                           from 'collections/ApiCollection';
import FormattedMessageWrapper                     from 'components/App/FormattedMessageWrapper';
import { getIdFromIri }                            from 'utils/apiTools';
import UserModel                                   from 'models/UserModel';
import { EN, FR }                                  from 'constants/Languages';
import Select                                      from 'components/App/IntlSelect';
import standistRoles, { getStandistRoleIntlLabel } from 'constants/StandistRoleReference';
import StandModel                                  from 'models/StandModel';
import { messageErrorIntl }                        from 'utils/messageIntl';
import { messageSuccessIntl }                      from 'utils/messageIntl';

interface SendNotificationFormProps {
  externalSync: boolean;
  model: NotificationModel;
  notificationTag?: string | null;
  selectedRows: StandModel[];
  standCollectionFull: ApiCollection;
}

@observer
class SendNotificationForm extends AppFormComponent<SendNotificationFormProps> {
  public editorRef = React.createRef<RichTextEditorHandle>();

  public formRef: React.RefObject<FormInstance> = React.createRef<FormInstance>();

  public notificationCollection = new ApiCollection(NotificationModel);

  public notificationVariableCollection = new ApiCollection(NotificationVariableModel);

  public objectFieldRef = React.createRef<any>();

  public state: {
    errors: any[];
  } = {
      errors: [],
    };

  public async componentDidMount() {
    const { model } = this.props;

    this.props.setSaveButtonProps({ text: <FormattedMessageWrapper id="Envoyer" /> });

    let xLocale = null;

    // we take the first user to set the locale
    const usersIris = model.get('users');
    if (!!usersIris && !!usersIris.length) {
      const firstUserId = getIdFromIri(UserModel, usersIris[0]);
      if (firstUserId) {
        try {
          const user = new UserModel({ id: firstUserId });
          await user.fetch();
          xLocale = user.languagePreference;
        } catch {
          // nothing
        }
      }
    }

    this.formRef.current?.setFieldsValue({
      customStandistRoles: standistRoles,
      locale: xLocale,
    });

    await this.notificationCollection.list({
      headers: {
        'X-LOCALE': xLocale,
      },
    });

    this.prepareForm();

    await this.notificationVariableCollection.list();
  }

  public componentDidUpdate(prevProps: SendNotificationFormProps) {
    if (prevProps.notificationTag !== this.props.notificationTag) {
      this.prepareForm();
    }
  }

  public render() {
    return (
      <Form ref={this.formRef}>
        <Row>
          {this.state.errors.length > 0 && (
            <Col className="SendNotificationForm__errorsZone" span={24}>
              <ul>
                {this.state.errors.map(error => (
                  <li key={error}>{error}</li>
                ))}
              </ul>
            </Col>
          )}
        </Row>
        <Row>
          <Col lg={{ order: 2, span: 6 }} md={{ order: 1, span: 24 }}>
            <Form.Item
              label={<FormattedMessageWrapper id="Langue" />}
              labelCol={{ span: 24 }}
              name="locale"
            >
              <Select
                onChange={this.handleLocaleChange}
                placeholder="Langue"
                style={{ width: '100%' }}
              >
                <Select.Option value={null}>
                  <FormattedMessageWrapper id="Aucune" />
                </Select.Option>
                <Select.Option value={FR.value}>{FR.label}</Select.Option>
                <Select.Option value={EN.value}>{EN.label}</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col
            className="SendNotificationForm__notifications"
            lg={{ order: 1, span: 18 }}
            md={{ order: 2, span: 24 }}
          >
            <Form.Item
              label={<FormattedMessageWrapper id="Modèle de notification" />}
              labelCol={{ span: 24 }}
              name="notification"
              rules={[
                {
                  message: <FormattedMessageWrapper id="Notification manquante" />,
                  required: true,
                },
              ]}
            >
              <Radio.Group>
                {this.notificationCollection.map((notification: NotificationModel) => (
                  <Radio.Button
                    key={notification.get('@id')}
                    onClick={() => this.loadNotificationInForm(notification)}
                    value={notification.id}
                  >
                    {notification.get('label')}
                  </Radio.Button>
                ))}
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col className="SendNotificationForm__variables" span={24}>
            <div className="SendNotificationForm__variablesLabel">
              <FormattedMessageWrapper id="Variables :" />
            </div>
            {this.notificationVariableCollection.map((variable: NotificationVariableModel) => (
              <Button key={variable.get('@id')} onClick={() => this.insertVariable(variable)}>
                {variable.get('label')}
              </Button>
            ))}
          </Col>
          <Col span={24}>
            <Form.Item
              label={<FormattedMessageWrapper id="Objet de la notification" />}
              labelCol={{ span: 24 }}
              name="customObject"
              rules={[
                {
                  message: <FormattedMessageWrapper id="Objet manquant" />,
                  required: true,
                },
              ]}
            >
              <Input
                ref={this.objectFieldRef}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={<FormattedMessageWrapper id="Envoyer à" />}
              labelCol={{ span: 24 }}
              name="customStandistRoles"
              rules={[
                {
                  message: <FormattedMessageWrapper id="Information manquante" />,
                  required: true,
                },
              ]}
            >
              <Select mode="multiple">
                {standistRoles.map(sr => (
                  <Select.Option
                    id={sr}
                    key={sr}
                    value={sr}
                  >
                    {getStandistRoleIntlLabel(sr)}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col className="SendNotificationForm__fieldNotificationContent" span={24}>
            <Form.Item
              label={<FormattedMessageWrapper id="Contenu de la notification" />}
              labelCol={{ span: 24 }}
              name="customContent"
              rules={[
                {
                  message: <FormattedMessageWrapper id="Contenu manquant" />,
                  required: true,
                },
              ]}
            >
              <RichTextEditor
                ref={this.editorRef}
                style={{
                  height: '256px',
                  marginBottom: '32px'
                }}
              />
            </Form.Item>
          </Col>
          <Col span={24}>

          </Col>
        </Row>
      </Form>
    );
  }

  public submit = async () => {
    const { model, onSuccess } = this.props;

    const values = await this.formRef.current?.validateFields();

    model.set('id', values.notification);
    model.set(values);

    const targets =
      this.props.selectedRows.length > 0 ? this.props.selectedRows : this.props.standCollectionFull;

    const stands = targets.map(target => target.get('@id'));

    model.set('stands', stands);

    try {
      await model.save();
      messageSuccessIntl("Notification envoyée !");
      if (onSuccess) {
        onSuccess();
      }
    } catch {
      messageErrorIntl("Echec envoi notification");
    }
  };

  private handleLocaleChange = async (locale: string) => {
    await this.notificationCollection.list({
      headers: {
        'X-LOCALE': locale,
      },
    });

    const notificationId = this.formRef.current?.getFieldValue('notification');

    if (notificationId) {
      const notification = this.notificationCollection.findBy('id', notificationId) as NotificationModel;

      if (notification) {
        this.loadNotificationInForm(notification);
      }
    }
  };

  private insertVariable = (variable: NotificationVariableModel) => {
    this.editorRef.current?.insertText(variable.get('tag'));
  };

  private loadNotificationInForm = (notification: NotificationModel) => {
    const { externalSync } = this.props;

    const contentExtOrInt = externalSync
      ? notification.get('contentExternal')
      : notification.get('contentInternal');

    this.formRef.current?.setFieldsValue({
      customContent: contentExtOrInt || '',
      customObject: notification.get('object'),
    });
  };

  private prepareForm = () => {
    const { notificationTag } = this.props;

    if (notificationTag) {
      const notificationFound = this.notificationCollection.find(
        notification => notification.get('tag') === notificationTag,
      ) as NotificationModel;

      if (notificationFound) {
        this.loadNotificationInForm(notificationFound);
        this.formRef.current?.setFieldsValue({
          notification: notificationFound.id,
        });
      }
    }
  };
}

export default SendNotificationForm;
